import {
  Paper,
  Grid,
  Button,
  CardActions,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Tooltip,
  Fab,
  SxProps,
  Checkbox,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { DeckDTO, PlanUnit, PlanWorkout, ResponseError, TrainingPlan } from '@/generated';
import { WorkoutCard } from './WorkoutCard';
import { createWorkout } from '../creationUtils';
import { useNavigate, useParams } from 'react-router-dom';
import { AppContext } from '@/contexts/app.context';
import { Delete } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { DefaultContainer } from '@/components/DefaultContainer';
import { DeleteDialog } from '@/components/DeleteDialog';
import { UpdateWorkoutUnitModal } from '../UpdateWorkoutUnitModal';
import EditIcon from '@mui/icons-material/Edit';
import { UpdateWorkoutModal } from '../UpdateWorkoutModal';
import { isPlanEditor, planHasOnlyCreatorUser } from '../utils';
import { ErrorComponent } from '@/components/ErrorComponent';
import { useListUserDecksQuery, usePlanQuery } from '@/hooks/query-hooks';
import { useUpdatePlan } from '@/hooks/plan-mutation.hooks';
import { PlanPermissionsElement } from '../PlanPermissionsIcon';
import { DeckAutocomplete } from '@/components/DeckAutocomplete';
import { pathMap } from '@/navigation/paths';
import { getDeckViewers } from '@/utils/deck';
import { isDeckOwner } from '@/screens/decks/deck-utils';
import { DragDropProvider } from '@dnd-kit/react';
import { isSortable, useSortable } from '@dnd-kit/react/sortable';
import { CurrentPlanStage } from '@/components/CurrentPlanStage';

const fabStyle = {
  position: 'fixed',
  bottom: 16,
  right: 16,
};

const fab = {
  color: 'primary' as 'primary',
  sx: fabStyle as SxProps,
  icon: <EditIcon />,
  label: 'Edit',
};

export const WorkoutUnit = () => {
  const context = useContext(AppContext);
  const { workoutUnitId, planId } = useParams();
  const { plan } = usePlanQuery(planId);
  const navigate = useNavigate();
  const localUser = context.state?.userProfile;
  const { decks } = useListUserDecksQuery(true);

  const group = plan?.units.find((unt) => unt.idClient === workoutUnitId);
  context.setExtraTitle(`: ${plan?.title} - ${group?.name}`);

  const [showCreateWorkoutDialog, setShowCreateWorkoutDialog] = useState(false);
  const [showEditWorkoutDialog, setShowEditWorkoutDialog] = useState(false);

  return (
    <DefaultContainer>
      <>
        <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
          <CreateWorkoutDialog
            group={group}
            plan={plan}
            open={showCreateWorkoutDialog}
            setOpen={(open) => {
              setShowCreateWorkoutDialog(open);
            }}
          />
          {plan && group && <UpdateWorkoutUnitModal plan={plan} workoutUnit={group} setShow={setShowEditWorkoutDialog} show={showEditWorkoutDialog} />}
          <Grid sx={{ alignContent: 'center', alignItems: 'center' }} container gap={2} direction={'row'}>
            <Stack sx={{ gap: 1 }}>
              <Stack sx={{ alignItems: 'center' }} gap={2} direction={'row'}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigate(pathMap.Plans);
                  }}
                >
                  &lt;&nbsp;&nbsp;Plans
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigate(`${pathMap.Plan}/${planId}`);
                  }}
                >
                  &lt;&nbsp;&nbsp;{plan?.title}
                </Button>
                <CurrentPlanStage name={group?.name} />
              </Stack>
            </Stack>
            <PlanPermissionsElement plan={plan} />
          </Grid>
        </Paper>
        {isPlanEditor(plan, localUser) && (
          <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
            <Tooltip title="Create new Workout.">
              <IconButton
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => {
                  setShowCreateWorkoutDialog(true);
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Paper>
        )}
        {decks && <WorkoutList decks={decks} plan={plan} unit={group} />}
      </>
      {isPlanEditor(plan, localUser) && (
        <Fab
          onClick={() => {
            setShowEditWorkoutDialog(true);
          }}
          sx={fab.sx}
          aria-label={fab.label}
          color={fab.color}
        >
          {fab.icon}
        </Fab>
      )}
    </DefaultContainer>
  );
};

export const WorkoutList = (props: { plan?: TrainingPlan; decks: DeckDTO[]; unit?: PlanUnit }) => {
  const { unit, decks, plan } = props;
  const context = useContext(AppContext);
  const localUser = context.state?.userProfile;
  const updatePlanMutation = useUpdatePlan();

  return (
    <DragDropProvider
      onDragEnd={async (event) => {
        const { source } = event.operation;

        if (!isSortable(source) || source.initialIndex === source.index || !plan || !unit) return;

        const workouts = [...unit.workouts];
        const [moved] = workouts.splice(source.initialIndex, 1);
        workouts.splice(source.index, 0, moved);

        await updatePlanMutation.mutateAsync({
          notifyUsers: false,
          plan: {
            ...plan,
            units: plan.units.map((planUnit) => (planUnit.idClient === unit.idClient ? { ...planUnit, workouts } : planUnit)),
          },
        });
      }}
    >
      <Grid container direction={'row'} spacing={2}>
        {unit?.workouts?.map((w: PlanWorkout, i) => (
          <WorkoutGridElement
            decks={decks}
            plan={plan}
            groupId={unit.idClient}
            id={w.idClient}
            index={i}
            key={w.idClient}
            sortingAllowed={isPlanEditor(plan, localUser)}
            workout={w}
          />
        ))}
      </Grid>
    </DragDropProvider>
  );
};

const WorkoutGridElement = (props: {
  plan?: TrainingPlan;
  decks: DeckDTO[];
  groupId: string;
  id: string;
  index: number;
  sortingAllowed: boolean;
  workout: PlanWorkout;
}) => {
  const { workout, decks, plan, groupId, id, index, sortingAllowed } = props;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const context = useContext(AppContext);
  const localUser = context.state?.userProfile;
  const useUpdatePlanMutation = useUpdatePlan();
  const { ref } = useSortable({ id, index, disabled: !sortingAllowed });

  return (
    <>
      <Grid ref={ref} size={{ xs: 12, md: 4 }}>
        <DeleteDialog
          title={`Really delete ${workout.name}`}
          text={`Really delete ${workout.name}`}
          open={showDeleteDialog}
          handleClose={async (deleted) => {
            if (deleted) {
              try {
                if (plan) {
                  await useUpdatePlanMutation.mutateAsync({
                    notifyUsers: false,
                    plan: {
                      ...plan,
                      units: plan.units.map((g) => ({ ...g, workouts: g.workouts.filter((wu) => wu.idClient !== workout.idClient) })),
                    },
                  });
                }
              } catch (e) {
                console.error(e);
              }
            }
            setShowDeleteDialog(false);
          }}
        />
        {plan && (
          <UpdateWorkoutModal
            show={showEditDialog}
            setShow={(show) => {
              setShowEditDialog(show);
            }}
            plan={plan}
            workout={workout}
            workoutUnitId={groupId}
          />
        )}
        <WorkoutCard decks={decks} groupId={groupId} planId={plan?.id} timeout={1000} workout={workout}>
          <CardActions sx={{ justifyContent: 'flex-end', alignSelf: 'flex-end', flex: 0.1 }}>
            {isPlanEditor(plan, localUser) && (
              <>
                <Tooltip title={`Edit ${workout.name}`}>
                  <IconButton
                    onClick={async () => {
                      setShowEditDialog(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Delete ${workout.name}`}>
                  <IconButton
                    onClick={async () => {
                      setShowDeleteDialog(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </CardActions>
        </WorkoutCard>
      </Grid>
    </>
  );
};

const CreateWorkoutDialog = (props: { plan?: TrainingPlan; group?: PlanUnit; open: boolean; setOpen: (open: boolean) => void }) => {
  const [workoutName, setWorkoutName] = useState<string>('');
  const { decks } = useListUserDecksQuery(true);
  const [selectedDeckId, setSelectedDeckId] = useState<string>();
  const [numberOfRounds, setNumberOfRounds] = useState<number>(3);
  const { group, plan } = props;
  const [error, setError] = useState<string>();
  const useUpdatePlanMutation = useUpdatePlan();
  const userId = useContext(AppContext).state?.userProfile?.userId;
  const [showOnlyMyDecks, setShowOnlyMyDecks] = useState<boolean>(false);
  const accessibleDecks = useMemo(
    () => decks?.filter((d) => plan?.viewerIds.every((id) => getDeckViewers(d).some((did) => id === did))).filter((d) => (showOnlyMyDecks ? isDeckOwner(d, userId) : true)) ?? [],
    [decks, plan?.viewerIds, showOnlyMyDecks],
  );
  console.log(showOnlyMyDecks);

  useEffect(() => {
    if (plan && userId && planHasOnlyCreatorUser(plan, userId)) setShowOnlyMyDecks(true);
    else setShowOnlyMyDecks(false);
  }, [plan, userId]);

  useEffect(() => {
    if (accessibleDecks && accessibleDecks.length) setSelectedDeckId(accessibleDecks[0].id);
  }, [accessibleDecks]);

  return (
    <Dialog open={props.open} onClose={() => props.setOpen(false)} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">{'Create new Workout'}</DialogTitle>
      <DialogContent>
        <Stack>
          <Stack direction={'row'} alignItems={'center'}>
            <Checkbox checked={showOnlyMyDecks} onChange={() => setShowOnlyMyDecks(!showOnlyMyDecks)} />
            <Typography>Show only my Decks</Typography>
          </Stack>
          <DeckAutocomplete decks={accessibleDecks} setSelectedDeckId={(deckId) => setSelectedDeckId(deckId)} selectedDeckId={selectedDeckId} />
          <TextField
            value={workoutName}
            placeholder="Name"
            label="Name"
            aria-label="Title"
            aria-describedby="basic-addon1"
            sx={{ my: 1 }}
            onChange={(e) => {
              setWorkoutName(e.currentTarget.value);
            }}
          />
          <TextField
            value={numberOfRounds}
            onChange={(v) => {
              const newValue = v.target.value.replace(`${numberOfRounds}`, '');
              let val = Number(newValue) ?? 3;
              if (val < 1) val = 1;
              if (val > 5) val = 5;
              setNumberOfRounds(val);
            }}
            type="number"
          ></TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={async () => {
            try {
              if (plan && selectedDeckId) {
                await useUpdatePlanMutation.mutateAsync({
                  notifyUsers: false,
                  plan: {
                    ...plan,
                    units: plan.units.map((g) => {
                      const lastSortKey = g?.workouts.reduce((p, c) => (p > c.sortKey ? p : c.sortKey), 0) ?? 0;
                      return g.idClient !== group?.idClient
                        ? g
                        : { ...group, workouts: [...group.workouts, createWorkout(workoutName, selectedDeckId, numberOfRounds, lastSortKey + 1)] };
                    }),
                  },
                });

                props.setOpen(false);
              }
            } catch (e) {
              if (e instanceof ResponseError) setError((await e.response.json()).message);
            }
          }}
        >
          Create
        </Button>
        <Button
          onClick={() => {
            setWorkoutName('');
            props.setOpen(false);
            setError(undefined);
          }}
        >
          Abort
        </Button>
      </DialogActions>
      {error && <ErrorComponent errors={[error]} />}
    </Dialog>
  );
};
